import type { QuotePayload } from "../api/QuoteSplitToCart";
import type { QuoteSummary, ProcessesedRows } from "../types/models";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import type { DetailedQuoteLineItemData } from "~/domains/orders/types/modelsV2";

interface QuotePreviewParams {
  items: ProcessesedRows[];
  quoteNumber: string;
}

export const getAccountInfo = (canSeeEndCustomer: boolean): string => {
  const userInfo = window.SCHA.user.selectedAccount;

  const selectAccountDetails = `${userInfo.name} (${userInfo.displayUid})`;
  const endCustomerDetails = `${userInfo.endCustomerName} (${userInfo.endCustomerCode})`;

  return canSeeEndCustomer ? `${selectAccountDetails} // ${endCustomerDetails}` : selectAccountDetails;
};

export const buildQuotePayload = (
  quotePreviewData: QuotePreviewParams[],
  actualQuoteDetails: QuoteSummary[],
): QuotePayload[] => {
  return quotePreviewData.map((entry) => {
    const quoteData = actualQuoteDetails.find((item) => item.quoteNumber === entry.quoteNumber);
    const products = quoteData?.quotesDetails.products ?? [];
    return {
      quoteNumber: entry.quoteNumber,
      items: entry.items.map((row) => {
        return makeItemsPayload(row, products);
      }),
    };
  });
};

export function findMainQuote(quoteLines: DetailedQuoteLineItemData[], position: number) {
  for (const item of quoteLines) {
    if (item.mainQuotes.length > 0) {
      const matchedMainQuote = item.mainQuotes.find((quote) => quote.lineNumber === position);
      if (matchedMainQuote) {
        return { product: item.product, mainQuote: matchedMainQuote };
      }
    }
    if (item.alternativeQuotes && item.alternativeQuotes.length > 0) {
      const alternateQuote = item.alternativeQuotes?.find((quote) => {
        return quote.lineNumber == position;
      });
      if (alternateQuote) {
        const matchedAlternateQuote = alternateQuote?.linesItem.find((line) => (line.lineNumber = position));
        return { product: item.product, mainQuote: matchedAlternateQuote };
      }
    }
  }

  throw new Error(`Main quote not found for position ${position}`);
}

export function makeItemsPayload(row: ProcessesedRows, quoteLines: DetailedQuoteLineItemData[]) {
  const matchedQuote = findMainQuote(quoteLines, row.position);

  const packageCode = matchedQuote?.mainQuote?.packCode ?? "";
  const originCountry = packageCode ? matchedQuote.product.fifteenDigit[packageCode].countryOfOrigin : "";

  return {
    productCode: matchedQuote.product.code,
    packageCode,
    packageSize: matchedQuote?.mainQuote?.packSize,
    quoteLine: matchedQuote?.mainQuote?.lineNo,
    quantity: row.quantity,
    confirmedQuantity: matchedQuote?.mainQuote?.confirmedQty,
    selectedDeliveryDate: row.date,
    selectedDeliveryDateIsManual: row.hasDateBeenModified,
    priceReference: matchedQuote?.mainQuote?.priceReference,
    isRestrictedPackage: isPackageRestrictionEnabled(),
    originCountry,
    currencyIso: matchedQuote?.mainQuote?.netPrice.currencyIso ?? "",
  };
}
